import { css } from 'lit';

export const baseVersionViewStyles = css`

  /*=== HOST ===*/

  :host {
    background: var(--gray-7);

    --theme-color-primary-0: color-mix(in hsl, var(--theme-color-primary) 10%, white);
    --theme-color-primary-1: color-mix(in hsl, var(--theme-color-primary) 15%, white);
    --theme-color-primary-2: color-mix(in hsl, var(--theme-color-primary) 20%, white);
    --theme-color-primary-3: color-mix(in hsl, var(--theme-color-primary) 50%, white);
    --theme-color-primary-4: color-mix(in hsl, var(--theme-color-primary) 80%, white);
    --theme-color-primary-5: color-mix(in hsl, var(--theme-color-primary) 100%, white);
    --theme-color-primary-6: color-mix(in hsl, var(--theme-color-primary) 80%, black);
    --theme-color-primary-7: color-mix(in hsl, var(--theme-color-primary) 50%, black);
    --theme-color-primary-8: color-mix(in hsl, var(--theme-color-primary) 30%, black);
    --theme-color-primary-9: color-mix(in hsl, var(--theme-color-primary) 15%, black);

    --theme-color-secondary-0: color-mix(in hsl, var(--theme-color-secondary) 10%, white);
    --theme-color-secondary-1: color-mix(in hsl, var(--theme-color-secondary) 15%, white);
    --theme-color-secondary-2: color-mix(in hsl, var(--theme-color-secondary) 20%, white);
    --theme-color-secondary-3: color-mix(in hsl, var(--theme-color-secondary) 50%, white);
    --theme-color-secondary-4: color-mix(in hsl, var(--theme-color-secondary) 80%, white);
    --theme-color-secondary-5: color-mix(in hsl, var(--theme-color-secondary) 100%, white);
    --theme-color-secondary-6: color-mix(in hsl, var(--theme-color-secondary) 80%, black);
    --theme-color-secondary-7: color-mix(in hsl, var(--theme-color-secondary) 50%, black);
    --theme-color-secondary-8: color-mix(in hsl, var(--theme-color-secondary) 30%, black);
    --theme-color-secondary-9: color-mix(in hsl, var(--theme-color-secondary) 15%, black);

    --section-gradient-start: var(--theme-color-primary-0);
    --section-gradient-end: var(--theme-color-primary-2);
    --section-border-color: var(--theme-color-primary-1);
    --section-item-bg: var(--gray-0);
    --section-item-color: var(--gray-9);
    --section-item-padding: 2rem;
  }

  view-content {
    padding: 0;
    margin-top: 50px;
    max-width: none;
  }

  /*=== SECTION LAYOUT ===*/

  section {
    padding: 0;
    margin: 0;
    
    border-bottom: 1px solid var(--section-border-color);
    background: linear-gradient(
      156deg,
      var(--section-gradient-start) 0%,
      var(--section-gradient-end) 100%
    );
  }

  section-content {
    display: grid;
    grid-template-columns: repeat(6, 1fr);

    max-width: calc(var(--q1-view-width) + (2 * var(--section-item-padding)));
    margin: 0 auto;
  }

  .section-item {
    padding: var(--section-item-padding);
    border: 1px solid var(--section-border-color);
    box-sizing: border-box;
    background: var(--section-item-bg);
    color: var(--section-item-color);
    margin: -1px; /* to overlap the borders */

    grid-column: span 6;
  }
  @media (min-width: 650px) {
    .section-item.half {
      grid-column: span 3;
    }
    .section-item.third {
      grid-column: span 2;
    }
  }

  .section-item h2, .section-item h3, .section-item h4 {
    margin-top: 0;
    margin-bottom: 1.5rem;
  }

  .section-item .label {
    font-size: 1rem;
    line-height: 1.3em;
    font-weight: 300;
    color: var(--gray-6);
    margin-bottom: 1rem;
  }

  .section-item blockquote {
    margin: 0;
    padding: 1.5rem;
    background: white;
    border-radius: 12px;
    border: 1px solid var(--gray-2);
  }
  .section-item blockquote cite {
    margin-top: 0.75rem;
    display: block;
  }
  .section-item blockquote .empty-placeholder {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    color: var(--gray-6);
    font-size: 1rem;
    line-height: 1.5em;
    font-weight: 300;
    font-style: italic;
  }

  .section-item simple-list .list-item:hover, simple-list .list-item:focus {
    background: white;
  }
  .section-item simple-list .list-item {
    padding: 1.5rem;
  }
  .section-item simple-list .list-item .item-header {
    float: right;
  }
  .section-item simple-list .list-item .item-body {
    margin-top: 0.25rem;
  }
  .section-item simple-list .list-item .item-footer {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-top: 1rem;
  }

  /*=== STANDARD SECTIONS ===*/

  section.header {
    --section-item-bg: var(--theme-color-primary-6);
    --section-item-color: var(--theme-text-color-primary);
  }
  section.header h1 {
    margin: 0;
  }
  section.header header-bar {
    padding-right: 6rem;
  }
  section.header header-bar .icon-wrapper {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    
    border-radius: 100rem;
    height: 4rem;
    width: 4rem;
    padding: 0;
    background: rgba(255, 255, 255, 0.3);
  }
  section.header header-bar sl-icon {
    font-size: 2.5rem;
  }
  section.header header-bar sl-copy-button#share-link {
    position: absolute;
    top: 1.3rem;
    right: var(--section-item-padding);
    color: white;
  }
  section.header header-bar sl-copy-button#share-link sl-icon {
    font-size: 2rem;
  }
  section.header header-bar sl-copy-button#share-link::part(success-icon) {
    color: var(--green-3);
  }
  section.header a {
    --link-color-default: var(--theme-text-color-primary);
    --link-color-visited: var(--theme-text-color-primary);
    --link-color-focus: var(--theme-text-color-primary);
  }

  section.error .section-item.notice-content .inner {
    position: relative;
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: 0.5rem;
    
    margin: 2rem auto 3rem auto;
    padding: 1.5rem 1.5rem 1.5rem 8rem;
    max-width: 18rem;
    
    color: var(--red-7);
    background: var(--red-1);
    border: 1px solid var(--red-2);
    border-radius: 7px;
  }
  @media (min-width: 650px) {
    section.error .section-item.notice-content .inner {
      margin: 4rem auto 14rem auto;
    }
  }

  section.error .section-item.notice-content h2,
  section.error .section-item.notice-content h3,
  section.error .section-item.notice-content p {
    margin: 0;
  }
  section.error .section-item.notice-content sl-icon {
    font-size: 5rem;
    position: absolute;
    opacity: 0.3;
    left: 1.5rem;
  }

  section.spacer .section-item {
    transition: opacity 0.2s cubic-bezier(0.65, 0.05, 0.36, 1);

    min-height: 0.8rem;
    margin: 0;
    padding: 0;
    background: var(--theme-color-secondary);
  }

  section.spacer .section-item.half { grid-column: span 3; }
  section.spacer .section-item.third { grid-column: span 2; }

  section.spacer .section-item:nth-child(1) { opacity: 0.05; }
  section.spacer .section-item:nth-child(2) { opacity: 0.13; }
  section.spacer .section-item:nth-child(3) { opacity: 0.17; }
  section.spacer .section-item:nth-child(4) { opacity: 0.42; }
  section.spacer .section-item:nth-child(5) { opacity: 0.22; }
  section.spacer .section-item:nth-child(6) { opacity: 0.36; }
  section.spacer .section-item:nth-child(7) { opacity: 0.28; }
  section.spacer .section-item:nth-child(8) { opacity: 0.39; }
  section.spacer .section-item:nth-child(9) { opacity: .11; }
  section.spacer .section-item:nth-child(10) { opacity: 0.05; }
  section.spacer .section-item:nth-child(11) { opacity: 0.13; }
  section.spacer .section-item:nth-child(12) { opacity: 0.17; }
  section.spacer .section-item:nth-child(13) { opacity: 0.42; }
  
  section.spacer .section-item:hover { opacity: 0.95; }

  /*=== SECTION ITEM TYPES ===*/

  .section-item.alert-content {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 1rem;

    padding-top: 1.5rem;
    padding-bottom: 1.5rem;
    font-weight: 400;

    --section-item-bg: var(--theme-color-secondary);
    color: var(--theme-text-color-secondary);
  }
  .section-item.alert-content > sl-icon {
    font-size: 1.7rem;
  }
  .section-item.alert-content .alert-message {
    flex: 1 1 auto;
  }
  .section-item.alert-content sl-button::part(base) {
    border-color: var(--theme-text-color-secondary);
    color: var(--theme-text-color-secondary);
  }

  .section-item.table-content table {
    margin-top: -0.5rem;
  }

  .section-item.data-content pre {
    padding: 1rem;
    background: var(--theme-color-primary-2);
    border-radius: 7px;
    border: 1px solid var(--theme-color-primary-3);
    color: var(--theme-color-primary-6);
    overflow: hidden;
  }
  
  .section-item code {
    background: var(--theme-color-primary-2);
    padding: 0.2rem 0.5rem;
    font-size: 0.85em;
    border-radius: 3px;
    color: var(--theme-color-primary-7);
  }

  /** Add this to the final item for extra padding. */
  .section-item.last-item {
    padding-bottom: 5rem;
  }

  /*=== INFO TABLE ===*/

  table.info-table {
    width: 100%;
    border-collapse: separate;
    border-spacing: 0;
    border: none;

    --table-edge-cell-padding: 0;
    --table-cell-padding: 0.5rem;
    --table-border: none;
    --table-cell-bg: transparent;
  }

  table.info-table td {
    font-size: 0.9rem;
    line-height: 1.3em;
    font-weight: 400;
    vertical-align: top;

    padding: var(--table-cell-padding);
    background: var(--table-cell-bg);
  }
  table.info-table td:first-child {
    padding-left: var(--table-edge-cell-padding);
  }
  table.info-table td:last-child {
    padding-right: var(--table-edge-cell-padding);
  }
  table.info-table tr:not(:last-child) td {
    border-bottom: var(--table-border);
  }

  table.info-table td.label-col {
    text-align: left;
    font-size: 0.85rem;
    line-height: 1.3em;
    font-weight: 300;
    color: var(--gray-6);

    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
  }

  table.info-table .td-inner {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.4rem;
  }

  /*=== SL-TAG COLORS ===*/

  sl-tag::part(base) {
    background-color: var(--tc-light);
    border-color: var(--tc-mid);
    color: var(--tc-dark);
  }

  /** Gray, medium lightness */
  sl-tag.draft {
    --tc-light: var(--gray-0);
    --tc-mid: var(--gray-4);
    --tc-dark: var(--gray-7);
  }

  /** Gray, super light */
  sl-tag.replaced,
  sl-tag.abandoned {
    --tc-light: var(--gray-0);
    --tc-mid: var(--gray-4);
    --tc-dark: var(--gray-6);
  }

  /** Gray, darkest */
  sl-tag.locked {
    --tc-light: var(--gray-1);
    --tc-mid: var(--gray-4);
    --tc-dark: var(--gray-7);
  }

  /** Theme secondary, bright. */
  sl-tag.proposed {
    --tc-light: var(--theme-color-secondary-0);
    --tc-mid: var(--theme-color-secondary-4);
    --tc-dark: var(--theme-color-secondary-6);
  }
  
  /** Green, super light. Not too saturated because it clashes with theme colors. */
  sl-tag.adopted,
  sl-tag.approved,
  sl-tag.auto-approved {
    --tc-light: var(--green-1);
    --tc-mid: var(--green-5);
    --tc-dark: var(--green-7);
  }

  /** Red, super light. Not too saturated because it clashes with theme colors. */
  sl-tag.declined {
    --tc-light: var(--red-0);
    --tc-mid: var(--red-2);
    --tc-dark: var(--red-7);
  }

`;
